import { useEffect, useState } from 'react';

import FileUploadIcon from '@mui/icons-material/FileUpload';
import { Box, DialogContent, Divider, Typography } from '@mui/material';
import { useAppDispatch } from 'app/hooks';
import { PopOver } from 'common/components/PopOver';
import { ImportJobStatusName, IMPORTS_FILE_NOT_CSV_ERROR, IMPORTS_FILE_NOT_CSV_ERROR_NOTE } from 'common/constants/admin';
import { postImportJob } from 'common/services/api/Imports/Imports.thunk';
import { ApplicationSuite, AppSuiteTenant, ImportJobStatus, ImportType } from 'common/types/Entities';
import { useSnackbar } from 'notistack';

import { usePollImportJobStatus } from '../hooks/usePollImportJobStatus';
import { allRequiredFieldForImportSet, getParsingErrorList, importAttemptCompleted, ImportJobStatusNameToDialogMessage, ImportJobStatusNameToDialogTitle, ImportNameToTypeMapper } from '../utils/ImportsUtils';

import { ImportsDialogContents } from './ImportsDialogContents';

type ImportsDialogProps = {
    importFile: File | null,
    importFileName: string | null,
    isOpen: boolean,
    tenant: AppSuiteTenant | null,
    appSuite: ApplicationSuite | null,
    importType: ImportType | null,
    handleClose: () => void
}

export function ImportsDialog({
    importFile,
    importFileName,
    isOpen,
    tenant,
    appSuite,
    importType,
    handleClose
}: Readonly<ImportsDialogProps>) {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [importStatus, setImportStatus] = useState<ImportJobStatus>({ importStatusName: ImportJobStatusName.UPLOADING });
    const [parsingErrors, setParsingErrors] = useState<Array<string> | null>(null);
    const [importJobId, setImportJobId] = useState<string | null>(null);
    const { pollImportJobStatus } = usePollImportJobStatus(setImportStatus, handleClose);

    useEffect(() => {
        if (allRequiredFieldForImportSet(importType, appSuite, tenant, importFileName, importFile) && importFile && importType) {
            const formData = new FormData();
            formData.append('file', importFile);
            const requestParams = {
                ...(tenant?.id && { tenantId: tenant.id }),
                importType: ImportNameToTypeMapper[importType.name],
                formData: formData
            };
            dispatch(postImportJob(requestParams))
                .unwrap()
                .then((data) => {
                    setImportJobId(data);
                })
                .catch((error) => {
                    if (error.message.includes(ImportJobStatusName.PARSING_ERRORS)) {
                        setImportStatus({ importStatusName: ImportJobStatusName.PARSING_ERRORS });
                        setParsingErrors(getParsingErrorList(error.message));
                        return;
                    } else if (error.message.includes(IMPORTS_FILE_NOT_CSV_ERROR)) {
                        setImportStatus({ importStatusName: ImportJobStatusName.PARSING_ERRORS });
                        setParsingErrors([IMPORTS_FILE_NOT_CSV_ERROR_NOTE]);
                        return;
                    }
                    enqueueSnackbar('An unhandled exception occured while attempting to import the file.', { variant: 'error' });
                    handleClose();
                });
        }
        else
        {
            enqueueSnackbar('Failed to start import. Import type, tenant, or file null.', { variant: 'error' });
            handleClose();
        }
    }, [importFile]);

    useEffect(() => {
        if (importJobId) {
            pollImportJobStatus(importJobId);
        }
    }, [importJobId]);

    return (<PopOver
        title={ImportJobStatusNameToDialogTitle[importStatus.importStatusName]}
        open={isOpen}
        showCloseIcon={importAttemptCompleted(importStatus.importStatusName)}
        onClose={importAttemptCompleted(importStatus.importStatusName) ? handleClose : undefined}
    >
        <DialogContent
            sx={{
                textAlign: 'left',
                padding: '1.5rem 2rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}
        >
            <Typography textAlign='center' color="info.light">
                {ImportJobStatusNameToDialogMessage[importStatus.importStatusName]}
            </Typography>
            {(importStatus.importStatusName != ImportJobStatusName.SUCCESS &&
              importStatus.importStatusName != ImportJobStatusName.CRITICAL_FAILURE)
             &&
                <>
                    <Divider style={{ width:'100%', marginTop: '1rem' }} />
                    <ImportsDialogContents
                        importStatus={importStatus}
                        parsingErrors={parsingErrors ?? []}
                    />
                </>
            }
        </DialogContent>
        <Box sx={{
            backgroundColor: 'primary.accent',
            height: '3rem',
            width: '3rem',
            borderRadius: '100%',
            position: 'absolute',
            left: '50%',
            transform: 'translate(-50%, -50%)',
        }}>
            <FileUploadIcon fontSize='large'
                sx={{
                    color: 'common.white',
                    marginTop: '7px',
                    marginLeft: '6px'
                }}
            />
        </Box>
    </PopOver>);
}
